import { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, ChevronRight } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', href: '#hero' },
    { name: 'Servicios', href: '#services' },
    { name: 'Nosotros', href: '#about' },
    { name: 'Galería', href: '#gallery' },
    { name: 'Contacto', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); scrollToSection('#hero'); }}
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src="/logo.png" 
              alt="MV Auto Expert Logo" 
              className="h-14 w-auto"
              data-testid="logo-image"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="text-zinc-300 hover:text-yellow-400 font-medium uppercase tracking-wider text-sm transition-colors"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Contact Info - Desktop */}
          <div className="hidden lg:flex items-center gap-6">
            <a 
              href="tel:+18628327369" 
              className="flex items-center gap-2 text-zinc-300 hover:text-yellow-400 transition-colors"
              data-testid="header-phone"
            >
              <Phone size={18} />
              <span className="text-sm font-medium">+1 (862) 832-7369</span>
            </a>
            <a
              href="#appointments"
              onClick={(e) => { e.preventDefault(); scrollToSection('#appointments'); }}
              className="bg-blue-600 text-white font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-blue-700 transition-all"
              data-testid="header-book-btn"
            >
              Agendar Cita
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden fixed inset-0 top-20 bg-zinc-950 z-40 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
              className="text-zinc-100 hover:text-yellow-400 font-bold uppercase tracking-wider text-2xl py-3 border-b border-zinc-800 flex items-center justify-between"
              data-testid={`mobile-nav-${link.name.toLowerCase()}`}
            >
              {link.name}
              <ChevronRight size={24} />
            </a>
          ))}
          <a
            href="#appointments"
            onClick={(e) => { e.preventDefault(); scrollToSection('#appointments'); }}
            className="bg-blue-600 text-white font-bold uppercase tracking-widest px-6 py-4 text-center mt-4 hover:bg-blue-700 transition-all"
            data-testid="mobile-book-btn"
          >
            Agendar Cita
          </a>
          <div className="mt-6 space-y-4 text-zinc-400">
            <a href="tel:+18628327369" className="flex items-center gap-3">
              <Phone size={20} className="text-yellow-400" />
              <span>+1 (862) 832-7369</span>
            </a>
            <div className="flex items-start gap-3">
              <MapPin size={20} className="text-yellow-400 mt-1" />
              <span>280 W Clinton St.<br />Dover, NJ 07801</span>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
